import { useEffect, useState } from 'react';
import ReactJson from 'react-json-view';
import { Select } from './Select';
import { Button } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug } from '@fortawesome/pro-solid-svg-icons';
import * as playerStores from '../../../api/stores/playerStores';
import { PlaycastDefaultProfiles } from '@playcastdotio/CoreTypes';
import { qualityManager, getPeer, devHardResetVideo } from '../../../api/modules/peer';
import config from '../../../config.json';

// Legacy peer imports — only used when USE_NEW_PEER is false
const PeerStatsComponent = !config.USE_NEW_PEER
  ? require('@playcastdotio/PeerComponents').PeerStatsComponent
  : undefined;
const PlaycastPeerClientType = !config.USE_NEW_PEER
  ? require('@playcastdotio/Peer').PlaycastPeerClient
  : undefined;
import * as globalStores from '../../../api/stores/globalStores';
import * as lobbyStores from '../../../api/stores/lobbyStores';
import { joinState, regionOverrideState } from '../../../api/stores/globalStores';
import { matchmakeToPublicMachineState } from '../../../api/stores/lobbyStores';
import { isMobileDevice } from '../../../utility/utility';
import { signupForPavilionLaunch } from '../../../api/modules/userEvents';
import { invitationDiagnosticsCache } from '../../../api/modules/realtimeMessaging/invitationDiagnosticsCache';

export const DevOverlay = () => {
  const [show, setShow] = useState(localStorage.getItem('devOverlay') === 'true' || false);
  const [reactStores, setReactStores] = useState({
    global: Object.fromEntries(Object.entries(globalStores).map(([key, value]) => [key, value.currentState])),
    lobby: Object.fromEntries(Object.entries(lobbyStores).map(([key, value]) => [key, value.currentState])),
    player: Object.fromEntries(
      Object.entries(playerStores).map(([key, value]) => {
        if ('currentState' in value) {
          return [key, value.currentState];
        }
        return [key, value];
      }),
    ),
  });
  const [shouldOverlay, setShouldOverlay] = useState(false);
  const [clientPeer, setClientPeer] = useState<any>();

  // Initialize all store listeners
  useEffect(() => {
    const listeners: Record<string, () => void> = {};

    // Setup global store listeners
    Object.entries(globalStores).forEach(([key, store]) => {
      if (store.addListener) {
        const listener = store.addListener((state) => {
          setReactStores((prev) => ({
            ...prev,
            global: { ...prev.global, [key]: state },
          }));
        });
        listeners[`global_${key}`] = () => store.removeListener(listener);
      }
    });

    // Setup lobby store listeners
    Object.entries(lobbyStores).forEach(([key, store]) => {
      if (store.addListener) {
        const listener = store.addListener((state) => {
          setReactStores((prev) => ({
            ...prev,
            lobby: { ...prev.lobby, [key]: state },
          }));
        });
        listeners[`lobby_${key}`] = () => store.removeListener(listener);
      }
    });

    // Setup player store listeners
    Object.entries(playerStores).forEach(([key, store]) => {
      if ('addListener' in store) {
        const listener = store.addListener((state) => {
          setReactStores((prev) => ({
            ...prev,
            player: { ...prev.player, [key]: state },
          }));
        });
        listeners[`player_${key}`] = () => store.removeListener(listener);
      }
    });

    // Setup quality manager listener
    const qualityListener = qualityManager.preferredQualityState.addListener((state) => {
      setReactStores((prev) => ({
        ...prev,
        qualityManager: state,
      }));
    });
    listeners.qualityManager = () => qualityManager.preferredQualityState.removeListener(qualityListener);

    // Setup join state listener
    const joinStateListener = joinState.addListener((state) => {
      setShouldOverlay(state.state === 'hostConnected');
      setClientPeer(getPeer());
    });
    setShouldOverlay(joinState.currentState.state === 'hostConnected');

    // Cleanup all listeners
    return () => {
      Object.values(listeners).forEach((removeListener) => removeListener());
      joinState.removeListener(joinStateListener);
    };
  }, []);

  // Handle keyboard shortcut for showing/hiding overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'F12') {
        localStorage.setItem('devOverlay', (!show).toString());
        setShow(!show);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show]);

  const handleDebugCopyToClipboard = () => {
    const redactedData = {
      ...reactStores,
      global: {
        ...reactStores.global,
        auth: reactStores.global.auth
          ? {
              ...reactStores.global.auth,
              accessToken: '<redacted>',
            }
          : null,
      },
    };
    const debugDump = `
---

<details>
<summary>
<h3>Debug Dump: Player</h3>
</summary>

\`\`\`json
${JSON.stringify(redactedData, null, 2)}
\`\`\`

Quality Control:
\`\`\`json
${JSON.stringify(qualityManager.preferredQualityState.currentState, null, 2)}
\`\`\`
</details>

---
`;
    navigator.clipboard.writeText(debugDump);
  };

  const floatingStyle: React.CSSProperties = {
    height: '100vh',
    overflowY: 'scroll',
    position: 'fixed',
    minWidth: '40vw',
    top: 0,
    left: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(11, 28, 44, 0.8)',
  };

  const [selectedQuality, setSelectedQuality] = useState<PlaycastDefaultProfiles | 'auto'>(
    qualityManager.preferredQualityState.currentState.auto ? 'auto' : qualityManager.preferredQualityState.currentState.profile,
  );
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(regionOverrideState.currentState.regionOverride);
  const [matchmakeToPublicMachine, setMatchmakeToPublicMachine] = useState<boolean>(
    matchmakeToPublicMachineState.currentState.matchmakeToPublicMachine,
  );
  const [diagnosticsCacheEnabled, setDiagnosticsCacheEnabled] = useState<boolean>(invitationDiagnosticsCache.isEnabled());

  useEffect(() => {
    const listener = qualityManager.preferredQualityState.addListener((state) => {
      setSelectedQuality(state.auto ? 'auto' : state.profile);
    });
    return () => {
      qualityManager.preferredQualityState.removeListener(listener);
    };
  }, []);

  useEffect(() => {
    const listener = regionOverrideState.addListener((state) => {
      setSelectedRegion(state.regionOverride || undefined);
    });
    return () => {
      regionOverrideState.removeListener(listener);
    };
  }, []);

  useEffect(() => {
    const listener = matchmakeToPublicMachineState.addListener((state) => {
      setMatchmakeToPublicMachine(state.matchmakeToPublicMachine);
    });
    return () => {
      matchmakeToPublicMachineState.removeListener(listener);
    };
  }, []);

  const dropdownItems: { label: string; value: PlaycastDefaultProfiles | 'auto' }[] = [
    {
      label: 'Auto',
      value: 'auto',
    },
    {
      label: '1080p',
      value: 'hostHigh',
    },
    {
      label: '720p',
      value: 'hostMedium',
    },
    {
      label: '360p',
      value: 'hostLow',
    },
  ];

  const regionDropdownItems = [
    {
      label: 'Clear Region Override',
      value: 'undefined',
    },
    {
      label: 'Auto',
      value: 'auto',
    },
    {
      label: 'US East 1',
      value: 'us-east-1',
    },
    // {
    //   label: 'US East 2',
    //   value: 'us-east-2',
    // },
    // {
    //   label: 'US West 1',
    //   value: 'us-west-1',
    // },
    {
      label: 'US West 2',
      value: 'us-west-2',
    },
  ];

  useEffect(() => {
    if (selectedQuality === 'auto') {
      const isMobile = isMobileDevice();
      qualityManager.setQuality(isMobile ? 'hostLow' : 'hostMedium', true);
    } else {
      qualityManager.setQuality(selectedQuality, false);
    }
  }, [selectedQuality]);

  useEffect(() => {
    if (selectedRegion === 'undefined') {
      regionOverrideState.updateState({ regionOverride: undefined });
    } else {
      regionOverrideState.updateState({ regionOverride: selectedRegion as 'auto' | 'us-east-1' | 'us-east-2' | 'us-west-1' | 'us-west-2' });
    }
  }, [selectedRegion]);

  const handleCloseClicked = () => {
    localStorage.setItem('devOverlay', 'false');
    setShow(false);
  };

  const handleOptInClicked = () => {
    signupForPavilionLaunch({
      announcements: true,
      earlyAccess: true,
      productUpdates: true,
    });
  };

  const handleMatchmakeToPublicToggle = () => {
    matchmakeToPublicMachineState.updateState({
      matchmakeToPublicMachine: !matchmakeToPublicMachine,
    });
  };

  const handleDiagnosticsCacheToggle = () => {
    const newEnabled = !diagnosticsCacheEnabled;
    invitationDiagnosticsCache.setEnabled(newEnabled);
    setDiagnosticsCacheEnabled(newEnabled);
  };

  if (!show) return null;

  return shouldOverlay ? (
    <div className='dev-overlay__content pb-4 pr-0' style={floatingStyle}>
      <div className='dev-overlay__section'>
        {/* Header with main controls */}
        <div className='mb-4 flex items-center justify-between border-b border-gray-600 p-2'>
          <h1 className='text-lg font-semibold text-white'>Dev Overlay</h1>
          <div className='flex gap-2'>
            <Button
              onClick={handleDiagnosticsCacheToggle}
              style={{
                backgroundColor: diagnosticsCacheEnabled ? '#22c55e' : '#6b7280',
                color: 'white',
                fontSize: '12px',
                padding: '4px 8px',
              }}
              title={`Diagnostics Cache: ${diagnosticsCacheEnabled ? 'Enabled' : 'Disabled'}`}
            >
              Cache
            </Button>
            <Button onClick={handleDebugCopyToClipboard}>
              <FontAwesomeIcon icon={faBug} />
            </Button>
            <Button onClick={handleCloseClicked}>Close</Button>
            <Button onClick={devHardResetVideo}>Reset Video</Button>
          </div>
        </div>

        {/* Two column layout - responsive: stacks on mobile */}
        <div className='flex h-full flex-col gap-4 lg:flex-row'>
          {/* Left Column - JSON and Peer Stats */}
          <div className='flex-1 overflow-auto'>
            <ReactJson
              name={false}
              src={reactStores}
              theme={'harmonic'}
              collapseStringsAfterLength={80}
              collapsed={1}
              indentWidth={2}
              style={{ fontSize: '16px', backgroundColor: 'rgba(0, 0, 0, 0)' }}
            />
            {!config.USE_NEW_PEER && clientPeer && PeerStatsComponent && (
              <div className='mt-4'>
                <PeerStatsComponent clientPeer={clientPeer} />
              </div>
            )}
          </div>

          {/* Right Column - Controls */}
          <div className='flex w-full flex-col gap-3 lg:w-80'>
            {/* Quality Control */}
            <div className='rounded-lg bg-slate-800 p-3'>
              <h3 className='mb-2 text-sm font-semibold text-white'>Quality</h3>
              <Select
                data={dropdownItems}
                selected={selectedQuality}
                onChange={(value) => setSelectedQuality(value as PlaycastDefaultProfiles)}
                placeholder='Quality'
              />
            </div>

            {/* Region Control */}
            <div className='rounded-lg bg-slate-800 p-3'>
              <h3 className='mb-2 text-sm font-semibold text-white'>Region</h3>
              <Select
                data={regionDropdownItems}
                selected={selectedRegion}
                onChange={(value) => setSelectedRegion(value as 'auto' | 'us-east-1' | 'us-east-2' | 'us-west-1' | 'us-west-2' | undefined)}
                placeholder='Region'
              />
            </div>

            {/* Public Matchmaking Toggle */}
            <div className='rounded-lg bg-slate-800 p-3'>
              <h3 className='mb-2 text-sm font-semibold text-white'>Public Host Matching</h3>
              <Button
                onClick={handleMatchmakeToPublicToggle}
                className='w-full'
                style={{
                  backgroundColor: matchmakeToPublicMachine ? '#22c55e' : '#ef4444',
                  color: 'black',
                }}
              >
                {matchmakeToPublicMachine ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className='dev-overlay p-4 text-white' style={{}}>
      {/* Header with main controls */}
      <div className='mb-4 flex items-center justify-between border-b border-gray-600 p-2'>
        <h1 className='text-lg font-semibold text-white'>Dev Overlay</h1>
        <div className='flex gap-2'>
          <Button
            onClick={handleDiagnosticsCacheToggle}
            style={{
              backgroundColor: diagnosticsCacheEnabled ? '#22c55e' : '#6b7280',
              color: 'white',
              fontSize: '12px',
              padding: '4px 8px',
            }}
            title={`Diagnostics Cache: ${diagnosticsCacheEnabled ? 'Enabled' : 'Disabled'}`}
          >
            Cache
          </Button>
          <Button onClick={handleDebugCopyToClipboard}>
            <FontAwesomeIcon icon={faBug} />
          </Button>
          <Button onClick={handleCloseClicked}>Close</Button>
        </div>
      </div>

      {/* Two column layout - responsive: stacks on mobile */}
      <div className='flex h-full flex-col gap-4 lg:flex-row'>
        {/* Left Column - JSON */}
        <div className='flex-1 overflow-auto'>
          <ReactJson
            name={false}
            src={reactStores}
            theme={'harmonic'}
            collapseStringsAfterLength={80}
            collapsed={1}
            indentWidth={2}
            style={{ fontSize: '16px' }}
          />
        </div>

        {/* Right Column - Controls */}
        <div className='flex w-full flex-col gap-3 lg:w-80'>
          {/* Mailing List */}
          <div className='rounded-lg bg-slate-800 p-3'>
            <h3 className='mb-2 text-sm font-semibold text-white'>Mailing List</h3>
            <Button onClick={handleOptInClicked} className='w-full'>
              Opt In
            </Button>
          </div>

          {/* Public Matchmaking Toggle */}
          <div className='rounded-lg bg-slate-800 p-3'>
            <h3 className='mb-2 text-sm font-semibold text-white'>Public Host Matching</h3>
            <Button
              onClick={handleMatchmakeToPublicToggle}
              className='w-full'
              style={{
                backgroundColor: matchmakeToPublicMachine ? '#22c55e' : '#ef4444',
                color: 'black',
              }}
            >
              {matchmakeToPublicMachine ? 'Enabled' : 'Disabled'}
            </Button>
          </div>

          {/* Region Control */}
          <div className='rounded-lg bg-slate-800 p-3'>
            <h3 className='mb-2 text-sm font-semibold text-white'>Region</h3>
            <Select
              data={regionDropdownItems}
              selected={selectedRegion}
              onChange={(value) => setSelectedRegion(value as 'auto' | 'us-east-1' | 'us-east-2' | 'us-west-1' | 'us-west-2' | undefined)}
              placeholder='Region'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
